import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { useLanguage } from '../i18n/LanguageContext';

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(40px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeInLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-40px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const fadeInRight = keyframes`
  from {
    opacity: 0;
    transform: translateX(40px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const scaleIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const AboutContainer = styled.div`
  padding: 140px 0 60px;
  background: #ffffff;
  min-height: 100vh;
  
  @media (max-width: 768px) {
    padding: 100px 0 40px;
  }
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 5%;
`;

const PageTitle = styled.h1`
  text-align: center;
  font-size: 4.5rem;
  color: #2c2c2c;
  margin-bottom: 30px;
  
  letter-spacing: -2px;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -15px;
    left: 50%;
    transform: translateX(-50%);
    width: 100px;
    height: 4px;
    background: linear-gradient(90deg, #2B4B8C, #3B82C4);
    border-radius: 2px;
  }
  
  @media (max-width: 768px) {
    font-size: 3rem;
  }
`;

const PageSubtitle = styled.p`
  text-align: center;
  font-size: 1.4rem;
  color: #6c757d;
  margin-bottom: 120px;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.7;
  
`;



// Stats Section
const StatsSection = styled.section`
  margin-bottom: 120px;
  
  &.animate-in {
    .stats-grid .stat-item:nth-child(1) { animation: ${fadeInUp} 0.6s ease-out 0.1s both; }
    .stats-grid .stat-item:nth-child(2) { animation: ${fadeInUp} 0.6s ease-out 0.3s both; }
    .stats-grid .stat-item:nth-child(3) { animation: ${fadeInUp} 0.6s ease-out 0.5s both; }
    .stats-grid .stat-item:nth-child(4) { animation: ${fadeInUp} 0.6s ease-out 0.7s both; }
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 60px;
  text-align: center;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 40px;
  }
`;

const StatItem = styled.div`
  padding: 40px 20px;
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  border-radius: 20px;
  border: 1px solid rgba(43, 75, 140, 0.05);
  transition: all 0.4s ease;
  cursor: pointer;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 20px 40px rgba(43, 75, 140, 0.1);
    border-color: rgba(43, 75, 140, 0.15);
  }
  
  .number {
    font-size: 3.5rem;
    
    color: #2B4B8C;
    margin-bottom: 15px;
    letter-spacing: -2px;
  }
  
  .label {
    font-size: 1.1rem;
    color: #6c757d;
    font-weight: 400;
    text-transform: uppercase;
    letter-spacing: 1px;
  }
`;

// Vision Mission Section
const VisionMissionSection = styled.section`
  margin-bottom: 120px;
  
  &.animate-in {
    .vision-mission-grid .vm-card:nth-child(1) { animation: ${fadeInLeft} 0.8s ease-out 0.2s both; }
    .vision-mission-grid .vm-card:nth-child(2) { animation: ${fadeInRight} 0.8s ease-out 0.4s both; }
  }
`;

const SectionTitle = styled.h2`
  font-size: 3.5rem;
  
  color: #2c2c2c;
  margin-bottom: 80px;
  text-align: center;
  letter-spacing: -1.5px;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const VisionMissionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 80px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 60px;
  }
`;

const VMCard = styled.div`
  position: relative;
  padding: 60px 50px;
  background: linear-gradient(135deg, #0a1628 0%, #152238 50%, #1a2744 100%);
  border-radius: 20px;
  color: white;
  overflow: hidden;
  transition: all 0.4s ease;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 20% 80%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.05) 0%, transparent 50%);
    pointer-events: none;
  }
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 25px 50px rgba(43, 75, 140, 0.3);
  }
  
  .icon {
    font-size: 4rem;
    margin-bottom: 30px;
    opacity: 0.9;
  }
  
  h3 {
    font-size: 2.2rem;
    font-weight: 400;
    margin-bottom: 25px;
    letter-spacing: -0.5px;
    color: #ffffff;
  }
  
  p {
    font-size: 1.2rem;
    line-height: 1.7;
    
    opacity: 0.95;
    color: #ffffff;
  }
`;

// Values Section
const ValuesSection = styled.section`
  margin-bottom: 120px;
  
  &.animate-in {
    .values-grid .value-card:nth-child(1) { animation: ${scaleIn} 0.6s ease-out 0.1s both; }
    .values-grid .value-card:nth-child(2) { animation: ${scaleIn} 0.6s ease-out 0.3s both; }
    .values-grid .value-card:nth-child(3) { animation: ${scaleIn} 0.6s ease-out 0.5s both; }
    .values-grid .value-card:nth-child(4) { animation: ${scaleIn} 0.6s ease-out 0.7s both; }
  }
`;

const ValuesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 50px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

const ValueCard = styled.div`
  padding: 50px 40px;
  background: white;
  border-radius: 20px;
  border: 1px solid rgba(43, 75, 140, 0.08);
  transition: all 0.4s ease;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 4px;
    height: 100%;
    background: linear-gradient(180deg, #c9a96e, #b08d55);
    transform: scaleY(0);
    transition: transform 0.4s ease;
  }
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 20px 40px rgba(43, 75, 140, 0.12);
    border-color: rgba(43, 75, 140, 0.15);
  }
  
  &:hover::before {
    transform: scaleY(1);
  }
  
  .icon {
    font-size: 3rem;
    color: #c9a96e;
    margin-bottom: 25px;
  }
  
  h3 {
    font-size: 1.8rem;
    color: #2c2c2c;
    margin-bottom: 20px;
    font-weight: 500;
    letter-spacing: -0.5px;
  }
  
  p {
    color: #6c757d;
    line-height: 1.7;
    font-size: 1.1rem;
    
  }
`;

// History Section
const HistorySection = styled.section`
  margin-bottom: 120px;
  text-align: center;
  max-width: 900px;
  margin: 0 auto 120px;
  
  &.animate-in {
    animation: ${fadeInUp} 0.8s ease-out;
  }
  
  .history-content {
    background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
    padding: 60px;
    border-radius: 20px;
    border: 1px solid rgba(43, 75, 140, 0.05);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
    position: relative;
    
    &::before {
      content: '45';
      position: absolute;
      top: -20px;
      left: 50%;
      transform: translateX(-50%);
      font-size: 8rem;
      font-weight: 800;
      color: rgba(201, 169, 110, 0.1);
      z-index: 0;
      line-height: 1;
    }
  }
  
  h2 {
    font-size: 3.5rem;
    color: #2c2c2c;
    margin-bottom: 30px;
    letter-spacing: -1.5px;
    position: relative;
    z-index: 1;
    
    @media (max-width: 768px) {
      font-size: 2.5rem;
    }
  }
  
  p {
    font-size: 1.25rem;
    line-height: 1.8;
    color: #6c757d;
    position: relative;
    z-index: 1;
    
    @media (max-width: 768px) {
      font-size: 1.1rem;
    }
  }
`;

const About = () => {
  const [animatedSections, setAnimatedSections] = useState(new Set());
  const { t } = useLanguage();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setAnimatedSections(prev => new Set(prev).add(entry.target.id));
          }
        });
      },
      { threshold: 0.1 }
    );

    const sections = document.querySelectorAll('[data-animate]');
    sections.forEach(section => observer.observe(section));

    return () => observer.disconnect();
  }, []);

  return (
    <AboutContainer>
      <Container>
        <PageTitle>{t('about.title')}</PageTitle>
        <PageSubtitle>
          {t('about.subtitle')}
        </PageSubtitle>

        <HistorySection 
          id="history-section" 
          data-animate
          className={animatedSections.has('history-section') ? 'animate-in' : ''}
        >
          <div className="history-content">
            <h2>{t("about.historyTitle")}</h2>
            <p>
              {t("about.historyText")}
            </p>
          </div>
        </HistorySection>

        <StatsSection 
          id="stats-section" 
          data-animate
          className={animatedSections.has('stats-section') ? 'animate-in' : ''}
        >
          <StatsGrid className="stats-grid">
            <StatItem className="stat-item">
              <div className="number">45</div>
              <div className="label">{t('about.yearsExp')}</div>
            </StatItem>
            <StatItem className="stat-item">
              <div className="number">500+</div>
              <div className="label">{t('about.project')}</div>
            </StatItem>
            <StatItem className="stat-item">
              <div className="number">1000+</div>
              <div className="label">{t('about.customer')}</div>
            </StatItem>
            <StatItem className="stat-item">
              <div className="number">50+</div>
              <div className="label">{t('about.partner')}</div>
            </StatItem>
          </StatsGrid>
        </StatsSection>

        <VisionMissionSection 
          id="vm-section" 
          data-animate
          className={animatedSections.has('vm-section') ? 'animate-in' : ''}
        >
          <SectionTitle>{t('about.vmTitle')}</SectionTitle>
          <VisionMissionGrid className="vision-mission-grid">
            <VMCard className="vm-card">
              <div className="icon">🎯</div>
              <h3>{t('about.visionTitle')}</h3>
              <p>{t('about.visionText')}</p>
            </VMCard>
            <VMCard className="vm-card">
              <div className="icon">🚀</div>
              <h3>{t('about.missionTitle')}</h3>
              <p>{t('about.missionText')}</p>
            </VMCard>
          </VisionMissionGrid>
        </VisionMissionSection>

        <ValuesSection 
          id="values-section" 
          data-animate
          className={animatedSections.has('values-section') ? 'animate-in' : ''}
        >
          <SectionTitle>{t('about.valuesTitle')}</SectionTitle>
          <ValuesGrid className="values-grid">
            <ValueCard className="value-card">
              <div className="icon">⭐</div>
              <h3>{t('about.quality')}</h3>
              <p>{t('about.qualityText')}</p>
            </ValueCard>
            <ValueCard className="value-card">
              <div className="icon">🤝</div>
              <h3>{t('about.reliability')}</h3>
              <p>{t('about.reliabilityText')}</p>
            </ValueCard>
            <ValueCard className="value-card">
              <div className="icon">🌱</div>
              <h3>{t('about.sustainability')}</h3>
              <p>{t('about.sustainabilityText')}</p>
            </ValueCard>
            <ValueCard className="value-card">
              <div className="icon">💡</div>
              <h3>{t('about.innovation')}</h3>
              <p>{t('about.innovationText')}</p>
            </ValueCard>
          </ValuesGrid>
        </ValuesSection>


      </Container>
    </AboutContainer>
  );
};

export default About;